import React from 'react';
import deckGlyphSvgUrl from '../../Icons/deck-icon.svg';

// Language logos use external URLs (<img>) so SVG defs/gradient IDs never clash when many
// cards render the same icon. Devicon icons are MIT-licensed: https://github.com/devicons/devicon
const DEVICON = 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons';
const languageLogos = {
  'javascript': { type: 'url', url: `${DEVICON}/javascript/javascript-original.svg` },
  'python': { type: 'url', url: `${DEVICON}/python/python-original.svg` },
  'java': { type: 'url', url: `${DEVICON}/java/java-original.svg` },
  'c': { type: 'url', url: `${DEVICON}/c/c-original.svg` },
  'cpp': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg' },
  'csharp': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg' },
  'php': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg' },
  'ruby': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ruby/ruby-original.svg' },
  'go': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg' },
  'rust': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rust/rust-original.svg' },
  'swift': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg' },
  'kotlin': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg' },
  'typescript': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg' },
  'html': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
  'css': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
  'sql': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
  'bash': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bash/bash-original.svg' },
  'powershell': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/powershell/powershell-original.svg' },
  'dart': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dart/dart-original.svg' },
  'scala': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/scala/scala-original.svg' },
  'r': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/r/r-original.svg' },
  /** Generic / unknown language — bundled URL for same reason as python (safe with many instances). */
  'code': { type: 'url', url: deckGlyphSvgUrl }
};

/**
 * Map Firestore / UI `language` strings to `languageLogos` keys.
 * Avoids substring traps (e.g. "javascript".includes("java")) — use explicit phrases + first-token rules.
 */
export function normalizeLanguageIconKey(raw) {
  if (raw == null || String(raw).trim() === '') return '';
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, ' ');
  const collapsed = s.replace(/\s+/g, '');

  if (
    s === 'java script'
    || s === 'java-script'
    || s === 'ecmascript'
    || s === 'javascript'
    || s === 'js'
  ) {
    return 'javascript';
  }

  if (s === 'c') return 'c';
  if (s === 'c++' || s === 'c plus plus' || s === 'cplusplus' || collapsed === 'cplusplus') return 'cpp';
  if (s === 'c#' || s === 'c sharp' || s === 'csharp') return 'csharp';

  if (collapsed.startsWith('python') && (collapsed === 'python' || /^python\d/.test(collapsed))) {
    return 'python';
  }

  const aliases = {
    js: 'javascript',
    ts: 'typescript',
    py: 'python',
    rb: 'ruby',
    rs: 'rust',
    kt: 'kotlin',
    cs: 'csharp',
    golang: 'go',
    node: 'javascript',
    nodejs: 'javascript',
    tsx: 'typescript',
    jsx: 'javascript'
  };

  if (aliases[collapsed]) return aliases[collapsed];
  if (aliases[s]) return aliases[s];

  const first = s.split(/[\s_.-]+/)[0] || '';
  const k = aliases[first] || first;

  if (languageLogos[k]) return k;
  if (languageLogos[collapsed]) return collapsed;
  if (languageLogos[first]) return first;
  return '';
}

// Tag icon mapping with dynamic matching - more specific tag icons
const tagIconMapping = {
  // Algorithm & Data Structure related
  'algorithm': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'algorithms': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'data-structure': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg' },
  'data-structures': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg' },
  'array': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'arrays': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'string': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'strings': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'tree': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
  'trees': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
  'graph': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
  'graphs': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
  'linked-list': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'stack': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'queue': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'hash-table': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'heap': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },

  // Sorting Algorithms
  'sorting': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'selection-sort': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'bubble-sort': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'quick-sort': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'merge-sort': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'insertion-sort': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },

  // Programming Paradigms
  'dynamic-programming': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  'recursion': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  'searching': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'binary-search': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'linear-search': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },

  // Math & Logic
  'math': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  'mathematics': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  'bit-manipulation': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'bitwise': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  'logic': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },

  // Design Patterns & Architecture
  'design-pattern': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'design-patterns': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'singleton': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'factory': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'observer': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'mvc': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'mvvm': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },

  // Database & Storage
  'database': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
  'sql': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
  'nosql': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
  'mongodb': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
  'postgresql': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
  'redis': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg' },

  // Web Technologies
  'api': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
  'rest': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
  'graphql': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
  'http': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
  'https': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
  'websocket': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
  'html': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
  'css': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
  'javascript': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'typescript': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg' },
  'react': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
  'vue': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg' },
  'angular': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg' },
  'nodejs': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
  'express': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg' },
  'jquery': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jquery/jquery-original.svg' },

  // Testing & Quality
  'testing': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg' },
  'test': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg' },
  'unit-test': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg' },
  'integration-test': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg' },
  'tdd': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg' },
  'clean-code': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'refactoring': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'debugging': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },

  // Security & Performance
  'security': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg' },
  'authentication': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg' },
  'authorization': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg' },
  'encryption': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg' },
  'performance': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
  'optimization': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
  'caching': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg' },

  // DevOps & Tools
  'docker': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
  'kubernetes': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg' },
  'git': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
  'github': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg' },
  'gitlab': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gitlab/gitlab-original.svg' },
  'aws': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg' },
  'azure': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg' },
  'firebase': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg' },

  // Mobile & Desktop
  'mobile': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/android/android-original.svg' },
  'android': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/android/android-original.svg' },
  'ios': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg' },
  'flutter': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg' },
  'react-native': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },

  // Machine Learning & AI
  'machine-learning': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  'ai': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  'tensorflow': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg' },
  'pytorch': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg' },
  'numpy': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  'pandas': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },

  // Other Common Tags
  'oop': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'functional': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'async': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'promise': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'callback': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'closure': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'hoisting': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'prototype': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  'inheritance': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'polymorphism': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'encapsulation': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  'abstraction': { type: 'url', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' }
};

/**
 * IconRenderer - A reusable component for rendering language and tag icons
 * 
 * @param {Object} props - Component props
 * @param {string} props.name - The name of the language or tag to render an icon for
 * @param {string} props.type - The type of icon to render ('language', 'tag', or 'auto')
 * @param {string} props.size - The size of the icon ('small', 'medium', or 'large')
 * @param {string} props.className - Additional CSS classes to apply
 * @param {Object} props.style - Additional inline styles to apply
 * 
 * @example
 * // Render a language icon
 * <IconRenderer name="javascript" type="language" size="medium" />
 * 
 * // Render a tag icon
 * <IconRenderer name="react" type="tag" size="small" />
 * 
 * // Auto-detect type
 * <IconRenderer name="python" size="large" />
 * 
 * @returns {JSX.Element} The rendered icon component
 */
const IconRenderer = ({
  name,
  type = 'auto', // 'language', 'tag', or 'auto'
  size = 'medium', // 'small', 'medium', 'large'
  className = '',
  style = {}
}) => {
  const trimmed = name != null ? String(name).trim() : '';
  const searchName = trimmed.toLowerCase();
  const displayName = trimmed || 'icon';

  // Size mapping
  const sizeMap = {
    small: '16px',
    medium: '24px',
    large: '32px'
  };

  const iconSize = sizeMap[size] || '24px';

  // Check if className includes explore-grid-card-icon - if so, let CSS control size
  const shouldUseCSSForSize = className.includes('explore-grid-card-icon');

  let iconInfo = null;

  if (type === 'language') {
    let langKey = normalizeLanguageIconKey(trimmed);
    if (!langKey) langKey = 'code';
    if (languageLogos[langKey]) {
      iconInfo = languageLogos[langKey];
    }
  } else if (type === 'tag') {
    const mapping = tagIconMapping;
    if (mapping[searchName]) {
      iconInfo = mapping[searchName];
    } else {
      for (const [key, info] of Object.entries(mapping)) {
        if (searchName.includes(key) || key.includes(searchName)) {
          iconInfo = info;
          break;
        }
      }
      if (!iconInfo) {
        const words = searchName.split(/[-_\s]+/);
        for (const word of words) {
          if (mapping[word]) {
            iconInfo = mapping[word];
            break;
          }
        }
      }
    }
  } else {
    // auto: normalized language first, then exact language key, then tag heuristics
    const langKey = normalizeLanguageIconKey(trimmed);
    if (langKey && languageLogos[langKey]) {
      iconInfo = languageLogos[langKey];
    } else if (languageLogos[searchName]) {
      iconInfo = languageLogos[searchName];
    } else {
      const mapping = tagIconMapping;
      if (mapping[searchName]) {
        iconInfo = mapping[searchName];
      } else {
        for (const [key, info] of Object.entries(mapping)) {
          if (searchName.includes(key) || key.includes(searchName)) {
            iconInfo = info;
            break;
          }
        }
        if (!iconInfo) {
          const words = searchName.split(/[-_\s]+/);
          for (const word of words) {
            if (mapping[word]) {
              iconInfo = mapping[word];
              break;
            }
          }
        }
      }
    }
  }

  // Render the icon
  if (iconInfo) {
    // Build style object - omit width/height if CSS should control it
    const baseStyle = shouldUseCSSForSize ? {} : { width: iconSize, height: iconSize };
    const finalStyle = { ...baseStyle, ...style };

    if (iconInfo.type === 'local') {
      const IconComponent = iconInfo.component;
      return (
        <IconComponent
          style={finalStyle}
          className={className}
        />
      );
    } else if (iconInfo.type === 'url') {
      return (
        <img
          src={iconInfo.url}
          alt={displayName}
          style={finalStyle}
          className={className}
        />
      );
    }
  }

  const firstLetter =
    displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?';
  const colors = ['#4a90e2', '#f39c12', '#e74c3c', '#27ae60', '#9b59b6', '#1abc9c', '#e67e22', '#34495e'];
  const colorIndex = displayName.length % colors.length;

  // For tags, use a more specific fallback icon
  if (type === 'tag') {
    const baseStyle = shouldUseCSSForSize ? {} : { width: iconSize, height: iconSize };
    return (
      <div
        style={{
          ...baseStyle,
          borderRadius: '50%',
          backgroundColor: '#4a90e2', // Blue color that matches your theme
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size === 'small' ? '12px' : size === 'large' ? '18px' : '14px',
          fontWeight: '900',
          color: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
          ...style
        }}
        className={className}
        title={displayName}
      >
        #
      </div>
    );
  }

  const baseStyle = shouldUseCSSForSize ? {} : { width: iconSize, height: iconSize };
  return (
    <div
      style={{
        ...baseStyle,
        borderRadius: '50%',
        backgroundColor: colors[colorIndex],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size === 'small' ? '10px' : size === 'large' ? '16px' : '12px',
        fontWeight: 'bold',
        color: 'white',
        ...style
      }}
      className={className}
      title={displayName}
    >
      {firstLetter}
    </div>
  );
};

export default IconRenderer; 